import { useEffect, useRef } from "react";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  OnboardingAnalyticsEvent,
  OnboardingAnalyticsPageView,
} from "@/constants/analytics/Analytics";
import { TabPath } from "@/constants/Navigation";
import { OnboardingWelcomeTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import { useUpdateUserOnboardedStatusMutation } from "@/store/api/user";
import WelcomePage from "@/ui/components/onboarding/welcome/WelcomePage";
import type {
  ExpoVideoPlayerHandle,
  VideoPlaybackStatus,
} from "@/ui/composed/media/videoPlayer/adapter/ExpoVideoPlayer";
import { Button, FAIconName } from "@/ui/core";
import { ScreenContainer, VStack } from "@/ui/layout";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import { toAbsoluteTabPath } from "@/utils/navigation.util";
import { joinTestIDs } from "@/utils/string.util";

const Screen = () => {
  const { track, trackPageView } = useAnalytics();
  const { captureException } = useAppMonitor();
  const { showToast } = useToast();
  const videoRef = useRef<ExpoVideoPlayerHandle | null>(null);
  const [updateUserOnboardedStatus, { isLoading: isUpdatingOnboardedStatus }] =
    useUpdateUserOnboardedStatusMutation();

  useEffect(() => {
    trackPageView(OnboardingAnalyticsPageView.Welcome);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const baseTestID = OnboardingWelcomeTestID.Primary;

  const handleNext = async () => {
    track(OnboardingAnalyticsEvent.LetsGoPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Button,
      [AnalyticsFieldName.Source]: OnboardingAnalyticsPageView.Welcome,
    });

    const video = videoRef.current;
    if (!video) return;
    try {
      const status: VideoPlaybackStatus = await video.getStatusAsync();
      if (status.isPlaying) {
        await video.pauseAsync();
      }
    } catch {
      captureException(new Error("Video getStatusAsync failed"));
    }

    updateUserOnboardedStatus()
      .unwrap()
      .then(() => {
        router.replace(toAbsoluteTabPath(TabPath.Home));
      })
      .catch(() => {
        showToast({
          text: "Something went wrong!",
          variant: "error",
        });
      });
  };

  return (
    <ScreenContainer
      backgroundColor={ColorToken.Primary10}
      isScrollView
      isStackLayout
    >
      <VStack
        flex={1}
        gap={24}
        justifyContent="space-between"
        paddingBottom={LayoutToken.OnboardingScreenHorizontalPadding}
        paddingHorizontal={LayoutToken.OnboardingScreenHorizontalPadding}
        paddingTop={LayoutToken.ModalHeaderPadding}
      >
        <WelcomePage videoRef={videoRef} />
        <Button
          isFullWidth
          isLoading={isUpdatingOnboardedStatus}
          leftIcon={FAIconName.Check}
          onPress={handleNext}
          testID={joinTestIDs(baseTestID, "next")}
          title="Let's go"
        />
      </VStack>
    </ScreenContainer>
  );
};

// eslint-disable-next-line import-x/no-unused-modules -- File is used by expo-router file based navigation but TypeScript can not infer its usage
export default Screen;
